import Button from '@atoms/new/button';

import { GenAiMainContent } from '@components/Presentation/ai/gen-ai-main-content';
import { pushDataLayerForEvent } from '@lib/gtag';
import { generateSlides } from '@services/generative-ai';
import { setRosPreviewSession } from '@services/ros-preview.service';
import { prepareROSPayload } from '@services/utils.service';
import { createRosTemplate } from '@services/youtube-platform.service';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { wesiteTopicBuckets } from './audience-engagement-ai';

const slides = [
  {
    title: '',
    type: 'PDF Slide',
    options: [],
    presentation_slide_s3_key: 'sandbox/website/Slide+1.webp',
  },
];

export default function WebsiteSandboxPage() {
  const searchParams = useSearchParams();

  const [query, setQuery] = useState('');
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('zoom');

  const [result, setResult] = useState([]);

  useEffect(() => {
    if (searchParams?.has('query')) {
      const query = searchParams?.get('query') || '';
      setQuery(query);
    }

    if (searchParams?.has('platform')) {
      const platform = searchParams?.get('platform') || '';
      setPlatform(platform);
    }
  }, [searchParams]);

  const { mutateAsync, isError } = useMutation({
    mutationFn: () =>
      generateSlides({ topic: query, audience: 'Advance', page: 0 }),
  });

  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const fetchSlides = async (title: string) => {
    try {
      setLoading(true);
      setResult([]);
      pushDataLayerForEvent('ai_search_topic', { title }, false);

      const generatedSlidesRes = await mutateAsync();

      if (!generatedSlidesRes?.status) return;

      const transformedData = generatedSlidesRes?.entity.map((slide: any) => {
        const options =
          slide?.options &&
          slide?.options.map((option: string) => ({
            body: option,
          }));

        slide.options = options || [];
        slide.type = slide.feature;
        return slide;
      });

      const data = [
        ...slides,
        ...transformedData.slice(0, 3),
        {
          title: 'Blank-Signup',
          type: 'PDF Slide',
          options: [],
          presentation_slide_s3_key: 'sandbox/website/Blank-Signup.png',
        },
        ...transformedData.slice(3),
        {
          title: 'Blank-Signup',
          type: 'PDF Slide',
          options: [],
          presentation_slide_s3_key: 'sandbox/website/Blank-Signup.png',
        },
        {
          title: '',
          type: 'PDF Slide',
          options: [],
          presentation_slide_s3_key: 'sandbox/Sandbox Analytics Slide.webp',
        },
        {
          title: '',
          type: 'PDF Slide',
          options: [],
          presentation_slide_s3_key: 'sandbox/website/last-slide-sandbox.webp',
        },
      ];

      const payload = prepareROSPayload(
        {
          isSandboxTemplate: true,
          title,
        },
        data,
        'sandboxUserId',
        [],
        true
      );

      const templateRes = await createRosTemplate(payload);

      if (!templateRes?.status) return;
      setRosPreviewSession(
        `${templateRes?.entity.user_id}:${templateRes?.entity.id}`
      );

      router.push(`/sandbox/website-modal?platform=${platform}`);
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative bg-black w-full h-screen">
      <header className="bg-black m-auto max-w-[1280px] z-50 px-4 flex items-center justify-between h-16 sticky left-0 right-0 top-0">
        <img
          id="saLogoId"
          src="/images/logos/logo_tag_white.svg"
          className="max-w-[156px] block cursor-pointer"
          onClick={() => {
            pushDataLayerForEvent('sandbox_logo', {}, false);
            window.open('https://www.streamalive.com/', '_blank');
          }}
        />

        <Button
          style={{
            borderRadius: '36px',
          }}
          className="px-8.5 py-3.75 h-auto"
          onClick={() => {
            pushDataLayerForEvent('sandbox_signup_top_cta', {}, false);
            window.open(process.env.NEXT_PUBLIC_REGISTER_ROUTE, '_blank');
          }}
        >
          Sign up
        </Button>
      </header>
      <section className="dark w-full px-4 py-20 bg-black flex items-center justify-center">
        <div className="w-full md:max-w-200">
          <div className="mb-10">
            <h1 className="text-white text-center text-3.5xl font-semibold leading-8 mb-2">
              {'What’s your session about?'}
            </h1>
            <p className="text-white text-center text-base">
              Our AI will craft & simulate 😮 delightfully engaging
              interactions.
            </p>
          </div>

          <GenAiMainContent
            selectedBlocks={[]}
            isPending={loading}
            setGenerateNewIsLoading={() => {}}
            generateNewIsLoading={false}
            audience=""
            isError={isError}
            onDeleteAutoComplete={() => {}}
            result={[]}
            setPage={() => {}}
            setResult={() => {}}
            setSelectedBlocks={() => {}}
            setAudience={() => {}}
            topic={topic}
            query={query}
            setShowAutoComplete={() => {}}
            autocomplete={[]}
            isWebsite={true}
            fetchSlides={(query) => {
              pushDataLayerForEvent(
                'sandbox_form_submit',
                { title: query },
                false
              );
              fetchSlides(query);
            }}
            onAutocompleteClick={(value) => {
              pushDataLayerForEvent(
                'sandbox_predefined_topic',
                { topic: value.topic },
                false
              );
              setTopic(value.topic);
              setQuery(value.topic);
              fetchSlides(value.topic);
            }}
            tryOutHeaderTitle="Or, choose a topic"
            setTopic={setTopic}
            showResult={false}
            compact={false}
            showAutoComplete={false}
            onQueryChange={(value) => {
              setQuery(value);
            }}
            buckets={wesiteTopicBuckets}
          />

          <div className="text-white flex items-center justify-center flex-col md:flex-row gap-10">
            <p className="text-xl">Seen the StreamAlive magic?</p>
            <Button
              onClick={() => {
                pushDataLayerForEvent('sandbox_signup_bottom_cta', {}, false);
                window.open(process.env.NEXT_PUBLIC_REGISTER_ROUTE, '_blank');
              }}
              style={{
                borderRadius: '36px',
              }}
              className="px-8.5 py-3.75 h-auto bg-transparent border-white border hover:bg-white hover:text-black"
            >
              Get Your Free Account Now
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
